import type { Props } from 'davaux/jsx-runtime'
import type { StyleObject, StyleProps, UiSpacing } from './types.js'
import { cx, mergeStyle, pickStyleProps, spacing } from './types.js'

const ROOT = 'dv-table'
const WRAPPER = 'dv-table-wrapper'

/** Which edge of the table the caption element is rendered on. */
export type TableCaptionSide = 'top' | 'bottom'

/** Props for the Table component. */
export interface TableProps extends StyleProps {
  /** Applies alternating row background colors; `true` defaults to `'odd'`. */
  striped?: boolean | 'odd' | 'even'
  /** Applies a hover background to rows when the cursor is over them. */
  highlightOnHover?: boolean
  /** Positions the `<caption>` element above or below the table. */
  captionSide?: TableCaptionSide
  /** Adds a border around the entire table. */
  withTableBorder?: boolean
  /** Adds vertical borders between columns. */
  withColumnBorders?: boolean
  /** Shows horizontal borders between rows (enabled by default). */
  withRowBorders?: boolean
  /** CSS color value applied to all table border lines. */
  borderColor?: string
  /** Padding applied to the left and right of each cell. */
  horizontalSpacing?: UiSpacing
  /** Padding applied to the top and bottom of each cell. */
  verticalSpacing?: UiSpacing
  /** CSS `table-layout` algorithm: `'auto'` sizes columns by content, `'fixed'` by column widths. */
  layout?: 'auto' | 'fixed'
  /** Inline styles merged with generated CSS custom properties. */
  style?: Props['style']
  /** `<thead>`, `<tbody>`, `<tfoot>`, and other table child elements. */
  children?: Props['children']
  [key: string]: unknown
}

/** Renders a styled HTML table wrapped in a scroll container, with support for striping, borders, and spacing. */
export function Table({
  striped,
  highlightOnHover,
  captionSide,
  withTableBorder,
  withColumnBorders,
  withRowBorders = true,
  borderColor,
  horizontalSpacing,
  verticalSpacing,
  layout,
  class: className,
  style,
  children,
  ...rest
}: TableProps): Promise<string> {
  const cssVars: StyleObject = {}

  if (horizontalSpacing !== undefined)
    cssVars['--dv-table-hz-spacing'] = spacing(horizontalSpacing) ?? ''
  if (verticalSpacing !== undefined)
    cssVars['--dv-table-v-spacing'] = spacing(verticalSpacing) ?? ''
  if (borderColor !== undefined) cssVars['--dv-table-border-color'] = borderColor
  if (captionSide !== undefined) cssVars['--dv-table-caption-side'] = captionSide
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)

  return (
    <div class={WRAPPER}>
      <table
        class={cx(ROOT, className)}
        style={mergeStyle({ ...cssVars, ...styleObj }, style)}
        data-striped={striped ? (striped === true ? 'odd' : striped) : undefined}
        data-hover={highlightOnHover || undefined}
        data-with-table-border={withTableBorder || undefined}
        data-with-column-borders={withColumnBorders || undefined}
        data-without-row-borders={!withRowBorders || undefined}
        data-layout={layout}
        {...ownRest}
      >
        {children}
      </table>
    </div>
  )
}

// ── Subcomponents ─────────────────────────────────────────

function makeSimple(tag: string, cls: string) {
  return function SubComponent({
    class: className,
    style,
    children,
    ...rest
  }: StyleProps & {
    class?: string
    style?: Props['style']
    children?: Props['children']
    [key: string]: unknown
  }): Promise<string> {
    const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
    const Tag = tag as unknown as (p: Props) => Promise<string>
    return (
      <Tag class={cx(cls, className)} style={mergeStyle(styleObj, style)} {...ownRest}>
        {children}
      </Tag>
    )
  }
}

/** Renders a `<thead>` element with table header styling. */
export const TableThead = makeSimple('thead', 'dv-table-thead')
/** Renders a `<tbody>` element with table body styling. */
export const TableTbody = makeSimple('tbody', 'dv-table-tbody')
/** Renders a `<tfoot>` element with table footer styling. */
export const TableTfoot = makeSimple('tfoot', 'dv-table-tfoot')
/** Props for the TableTr component. */
export interface TableTrProps extends StyleProps {
  /** Marks the row as selected, applying the selected-row highlight. */
  selected?: boolean
  class?: string
  style?: Props['style']
  children?: Props['children']
  [key: string]: unknown
}

/** Renders a `<tr>` element with table row styling. */
export function TableTr({ selected, class: className, style, children, ...rest }: TableTrProps): Promise<string> {
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
  return (
    <tr
      class={cx('dv-table-tr', className)}
      style={mergeStyle(styleObj, style)}
      data-selected={selected || undefined}
      {...ownRest}
    >
      {children}
    </tr>
  )
}
/** Renders a `<caption>` element positioned by the parent table's `captionSide` setting. */
export const TableCaption = makeSimple('caption', 'dv-table-caption')

/** Props shared by `TableTh` and `TableTd` cell components. */
export interface TableCellProps extends StyleProps {
  /** Horizontal text alignment within the cell. */
  align?: 'left' | 'center' | 'right' | 'justify'
  /** Fixed width of the cell as a pixel number or CSS string. */
  width?: number | string
  /** Additional CSS class names to apply. */
  class?: string
  /** Inline styles merged with generated CSS custom properties. */
  style?: Props['style']
  /** Content rendered inside the cell. */
  children?: Props['children']
  [key: string]: unknown
}

function makeCell(tag: 'th' | 'td', cls: string) {
  return function CellComponent({
    align,
    width,
    class: className,
    style,
    children,
    ...rest
  }: TableCellProps): Promise<string> {
    const cssVars: StyleObject = {}
    if (width !== undefined)
      cssVars.width = typeof width === 'number' ? `${width}px` : width

    const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)
    const merged = { ...cssVars, ...styleObj }
    const Tag = tag as unknown as (p: Props) => Promise<string>
    return (
      <Tag
        class={cx(cls, className)}
        style={Object.keys(merged).length ? mergeStyle(merged, style) : style}
        align={align}
        {...ownRest}
      >
        {children}
      </Tag>
    )
  }
}

/** Renders a `<th>` header cell with optional alignment and fixed width. */
export const TableTh = makeCell('th', 'dv-table-th')
/** Renders a `<td>` data cell with optional alignment and fixed width. */
export const TableTd = makeCell('td', 'dv-table-td')

Table.Thead = TableThead
Table.Tbody = TableTbody
Table.Tfoot = TableTfoot
Table.Tr = TableTr
Table.Th = TableTh
Table.Td = TableTd
Table.Caption = TableCaption
